/**
 * @module @liquidefy/code-editor/terminal/guided/hints
 * @version 1.0.0
 * @license LQD Research LLC
 * @rust hints.rs — false; 4/2/26; sm
 *
 * Contextual command hints — pre-execution warnings, typing assistance,
 * post-error fix suggestions, and structured command explanations.
 * All functions are pure. No DOM, no side effects.
 */

// ═══ §1 TYPES ═══

export type HintType = 'pre-execution' | 'typing' | 'post-error' | 'post-success' | 'flag-info' | 'safety-warning';
export type HintSeverity = 'info' | 'suggestion' | 'warning' | 'danger';

export interface CommandHint {
  readonly type: HintType;
  readonly severity: HintSeverity;
  readonly title: string;
  readonly body: string;
  readonly command: string;
  readonly relatedDocs?: string;
  readonly suggestedAlternative?: string;
  readonly dismissed: boolean;
}

export interface HintState {
  readonly enabled: boolean;
  readonly hints: CommandHint[];
  readonly suppressedCommands: string[];
  readonly detailLevel: 'minimal' | 'standard' | 'verbose';
}

export interface FlagInfo {
  readonly flag: string;
  readonly description: string;
  readonly isDestructive: boolean;
  readonly isSudo: boolean;
}

export interface CommandExplanation {
  readonly command: string;
  readonly description: string;
  readonly flags: FlagInfo[];
  readonly arguments: Array<{ name: string; description: string }>;
  readonly riskLevel: 'safe' | 'moderate' | 'dangerous';
  readonly affectedScope: string;
}

export interface HintSummary {
  readonly total: number;
  readonly byType: Record<HintType, number>;
  readonly bySeverity: Record<HintSeverity, number>;
}

// ═══ §2 KNOWLEDGE BASE ═══

interface CommandInfo {
  readonly description: string;
  readonly risk: 'safe' | 'moderate' | 'dangerous';
  readonly scope: string;
  readonly commonFlags: Record<string, FlagInfo>;
  readonly typingHint?: string;
}

const COMMANDS: Record<string, CommandInfo> = {
  rm:      { description: 'Remove files or directories', risk: 'dangerous', scope: 'specified files/directories',
             commonFlags: { '-r': { flag: '-r', description: 'Remove directories and their contents recursively', isDestructive: true, isSudo: false },
                            '-f': { flag: '-f', description: 'Force removal without confirmation prompts', isDestructive: true, isSudo: false },
                            '-i': { flag: '-i', description: 'Prompt before each removal', isDestructive: false, isSudo: false },
                            '-rf': { flag: '-rf', description: 'Recursively force-delete without prompts', isDestructive: true, isSudo: false } },
             typingHint: 'Remove files. Use -i for interactive mode. Careful with -rf.' },
  chmod:   { description: 'Change file permissions', risk: 'moderate', scope: 'specified files',
             commonFlags: { '-R': { flag: '-R', description: 'Change permissions recursively', isDestructive: false, isSudo: false },
                            '777': { flag: '777', description: 'Give read/write/execute to everyone (insecure)', isDestructive: false, isSudo: false } },
             typingHint: 'Change file permissions. Common: chmod 755 file, chmod +x script' },
  chown:   { description: 'Change file owner and group', risk: 'moderate', scope: 'specified files',
             commonFlags: { '-R': { flag: '-R', description: 'Change ownership recursively', isDestructive: false, isSudo: true } },
             typingHint: 'Change file ownership. Usually requires sudo.' },
  mv:      { description: 'Move or rename files and directories', risk: 'moderate', scope: 'source and destination paths',
             commonFlags: { '-i': { flag: '-i', description: 'Prompt before overwriting', isDestructive: false, isSudo: false },
                            '-n': { flag: '-n', description: 'Do not overwrite existing files', isDestructive: false, isSudo: false } },
             typingHint: 'Move/rename files. mv source destination' },
  cp:      { description: 'Copy files and directories', risk: 'safe', scope: 'source and destination paths',
             commonFlags: { '-r': { flag: '-r', description: 'Copy directories recursively', isDestructive: false, isSudo: false },
                            '-i': { flag: '-i', description: 'Prompt before overwriting', isDestructive: false, isSudo: false } },
             typingHint: 'Copy files. Use -r for directories.' },
  ls:      { description: 'List directory contents', risk: 'safe', scope: 'current or specified directory',
             commonFlags: { '-la': { flag: '-la', description: 'Long format with hidden files', isDestructive: false, isSudo: false },
                            '-lh': { flag: '-lh', description: 'Long format with human-readable sizes', isDestructive: false, isSudo: false } },
             typingHint: 'List files. Common: ls -la, ls -lh' },
  cat:     { description: 'Display file contents', risk: 'safe', scope: 'specified files',
             commonFlags: { '-n': { flag: '-n', description: 'Number all output lines', isDestructive: false, isSudo: false } },
             typingHint: 'Display file contents. cat filename' },
  grep:    { description: 'Search text using patterns', risk: 'safe', scope: 'specified files or stdin',
             commonFlags: { '-r': { flag: '-r', description: 'Search recursively in directories', isDestructive: false, isSudo: false },
                            '-i': { flag: '-i', description: 'Case-insensitive search', isDestructive: false, isSudo: false },
                            '-n': { flag: '-n', description: 'Show line numbers', isDestructive: false, isSudo: false } },
             typingHint: 'Search for patterns in files. grep -rn "pattern" .' },
  find:    { description: 'Search for files in directory hierarchy', risk: 'safe', scope: 'specified directory tree',
             commonFlags: { '-name': { flag: '-name', description: 'Match by filename pattern', isDestructive: false, isSudo: false },
                            '-type': { flag: '-type', description: 'Filter by type (f=file, d=directory)', isDestructive: false, isSudo: false },
                            '-exec': { flag: '-exec', description: 'Execute command on each match', isDestructive: false, isSudo: false },
                            '-delete': { flag: '-delete', description: 'Delete matching files', isDestructive: true, isSudo: false } },
             typingHint: 'Find files by name, type, size, date. find . -name "*.ts"' },
  git:     { description: 'Git version control', risk: 'safe', scope: 'current repository',
             commonFlags: {},
             typingHint: 'Git version control. Common: status, add, commit, push, pull, log' },
  docker:  { description: 'Docker container management', risk: 'moderate', scope: 'Docker daemon',
             commonFlags: {},
             typingHint: 'Docker containers. Common: build, run, ps, logs, stop, rm' },
  npm:     { description: 'Node.js package manager', risk: 'safe', scope: 'current project',
             commonFlags: {},
             typingHint: 'Node packages. Common: install, run, test, build' },
  pnpm:    { description: 'Fast Node.js package manager', risk: 'safe', scope: 'current project',
             commonFlags: {},
             typingHint: 'Fast Node packages. Common: install, run, test, build' },
  yarn:    { description: 'Node.js package manager', risk: 'safe', scope: 'current project',
             commonFlags: {},
             typingHint: 'Node packages. Common: add, run, test, build' },
  pip:     { description: 'Python package manager', risk: 'safe', scope: 'Python environment',
             commonFlags: {},
             typingHint: 'Python packages. Common: install, uninstall, list, freeze' },
  curl:    { description: 'Transfer data from/to a server', risk: 'safe', scope: 'network request',
             commonFlags: { '-o': { flag: '-o', description: 'Write output to file', isDestructive: false, isSudo: false },
                            '-X': { flag: '-X', description: 'Specify HTTP method', isDestructive: false, isSudo: false } },
             typingHint: 'Make HTTP requests. curl -X GET https://...' },
  wget:    { description: 'Download files from the web', risk: 'safe', scope: 'network download',
             commonFlags: { '-O': { flag: '-O', description: 'Output to file', isDestructive: false, isSudo: false } },
             typingHint: 'Download files. wget https://...' },
  sudo:    { description: 'Execute command as superuser', risk: 'dangerous', scope: 'system-wide',
             commonFlags: {},
             typingHint: 'Run as superuser. Use with caution.' },
  kill:    { description: 'Send signal to a process', risk: 'moderate', scope: 'specified process',
             commonFlags: { '-9': { flag: '-9', description: 'Force kill (SIGKILL) — no cleanup', isDestructive: true, isSudo: false },
                            '-15': { flag: '-15', description: 'Graceful termination (SIGTERM)', isDestructive: false, isSudo: false } },
             typingHint: 'Send signal to process. kill -9 PID for force kill.' },
  killall: { description: 'Kill processes by name', risk: 'dangerous', scope: 'all matching processes',
             commonFlags: { '-9': { flag: '-9', description: 'Force kill all matching', isDestructive: true, isSudo: false } },
             typingHint: 'Kill all processes by name. killall processname' },
  ps:      { description: 'Report process status', risk: 'safe', scope: 'system processes',
             commonFlags: { 'aux': { flag: 'aux', description: 'Show all processes with details', isDestructive: false, isSudo: false } },
             typingHint: 'List running processes. ps aux' },
  top:     { description: 'Display system resource usage', risk: 'safe', scope: 'system', commonFlags: {},
             typingHint: 'Live system resource monitor.' },
  df:      { description: 'Report disk space usage', risk: 'safe', scope: 'all mounted filesystems',
             commonFlags: { '-h': { flag: '-h', description: 'Human-readable sizes', isDestructive: false, isSudo: false } },
             typingHint: 'Show disk space. df -h' },
  du:      { description: 'Estimate file/directory space usage', risk: 'safe', scope: 'specified path',
             commonFlags: { '-sh': { flag: '-sh', description: 'Summary in human-readable format', isDestructive: false, isSudo: false } },
             typingHint: 'Directory sizes. du -sh *' },
  tar:     { description: 'Archive files', risk: 'safe', scope: 'specified files',
             commonFlags: { '-xzf': { flag: '-xzf', description: 'Extract gzipped tar archive', isDestructive: false, isSudo: false },
                            '-czf': { flag: '-czf', description: 'Create gzipped tar archive', isDestructive: false, isSudo: false } },
             typingHint: 'Archive/extract. tar -czf archive.tar.gz dir/ or tar -xzf archive.tar.gz' },
  ssh:     { description: 'Secure shell remote login', risk: 'safe', scope: 'remote server',
             commonFlags: {},
             typingHint: 'Remote login. ssh user@host' },
  scp:     { description: 'Secure copy over SSH', risk: 'safe', scope: 'local and remote paths',
             commonFlags: { '-r': { flag: '-r', description: 'Copy directories recursively', isDestructive: false, isSudo: false } },
             typingHint: 'Copy files over SSH. scp file user@host:path' },
  rsync:   { description: 'Remote/local file synchronization', risk: 'moderate', scope: 'source and destination',
             commonFlags: { '-avz': { flag: '-avz', description: 'Archive mode, verbose, compressed', isDestructive: false, isSudo: false },
                            '--delete': { flag: '--delete', description: 'Delete files at destination not in source', isDestructive: true, isSudo: false },
                            '--dry-run': { flag: '--dry-run', description: 'Show what would be transferred', isDestructive: false, isSudo: false } },
             typingHint: 'Sync files. rsync -avz source/ dest/' },
  mkfs:    { description: 'Create a filesystem on a device', risk: 'dangerous', scope: 'specified device',
             commonFlags: {},
             typingHint: 'Format a disk partition. DESTRUCTIVE.' },
  dd:      { description: 'Low-level data copy', risk: 'dangerous', scope: 'specified devices/files',
             commonFlags: {},
             typingHint: 'Low-level block copy. Can overwrite disks. Extremely dangerous.' },
  systemctl: { description: 'Control system services', risk: 'moderate', scope: 'system services',
               commonFlags: {},
               typingHint: 'Manage services. systemctl start|stop|restart|status service' },
  iptables:  { description: 'Configure firewall rules', risk: 'dangerous', scope: 'system network',
               commonFlags: { '-F': { flag: '-F', description: 'Flush all rules', isDestructive: true, isSudo: true } },
               typingHint: 'Firewall rules. Requires sudo. Be very careful.' },
  cd:      { description: 'Change working directory', risk: 'safe', scope: 'current shell session',
             commonFlags: {},
             typingHint: 'Change directory. cd path, cd ~, cd ..' },
  pwd:     { description: 'Print working directory', risk: 'safe', scope: 'current shell',
             commonFlags: {},
             typingHint: 'Show current directory path.' },
  echo:    { description: 'Display text or variables', risk: 'safe', scope: 'stdout',
             commonFlags: {},
             typingHint: 'Print text. echo "hello" or echo $VARIABLE' },
  mkdir:   { description: 'Create directories', risk: 'safe', scope: 'specified path',
             commonFlags: { '-p': { flag: '-p', description: 'Create parent directories as needed', isDestructive: false, isSudo: false } },
             typingHint: 'Create directories. mkdir -p path/to/dir' },
  touch:   { description: 'Create empty file or update timestamp', risk: 'safe', scope: 'specified file',
             commonFlags: {},
             typingHint: 'Create empty file. touch filename' },
  head:    { description: 'Output first lines of a file', risk: 'safe', scope: 'specified file',
             commonFlags: { '-n': { flag: '-n', description: 'Number of lines to show', isDestructive: false, isSudo: false } },
             typingHint: 'Show first lines. head -n 20 file' },
  tail:    { description: 'Output last lines of a file', risk: 'safe', scope: 'specified file',
             commonFlags: { '-f': { flag: '-f', description: 'Follow file as it grows', isDestructive: false, isSudo: false },
                            '-n': { flag: '-n', description: 'Number of lines to show', isDestructive: false, isSudo: false } },
             typingHint: 'Show last lines. tail -f file for live updates.' },
  less:    { description: 'View file contents page by page', risk: 'safe', scope: 'specified file',
             commonFlags: {},
             typingHint: 'Page through file contents. q to quit.' },
  wc:      { description: 'Count lines, words, characters', risk: 'safe', scope: 'specified files',
             commonFlags: { '-l': { flag: '-l', description: 'Count lines only', isDestructive: false, isSudo: false } },
             typingHint: 'Count lines/words. wc -l file' },
};

interface DangerousPattern {
  readonly pattern: RegExp;
  readonly title: string;
  readonly body: string;
  readonly severity: HintSeverity;
  readonly alternative?: string;
}

const DANGEROUS_PATTERNS: DangerousPattern[] = [
  { pattern: /rm\s+(-[a-zA-Z]*f[a-zA-Z]*\s+|--force\s+)\/\s*$/,  title: 'CRITICAL: Deleting root filesystem', body: 'This will attempt to delete your entire filesystem. This is almost certainly not what you want.', severity: 'danger' },
  { pattern: /rm\s+-[a-zA-Z]*r[a-zA-Z]*f|rm\s+-[a-zA-Z]*f[a-zA-Z]*r/, title: 'Recursive forced deletion', body: 'rm -rf deletes files and directories without confirmation. Double-check the target path.', severity: 'warning', alternative: 'rm -ri (interactive) or use trash-cli instead' },
  { pattern: /rm\s+-[a-zA-Z]*r/, title: 'Recursive deletion', body: 'Deleting directories recursively. Verify the target path is correct.', severity: 'warning', alternative: 'rm -ri for interactive confirmation' },
  { pattern: /chmod\s+777/,     title: 'Insecure permissions', body: 'chmod 777 gives read/write/execute to everyone. This is a security risk.', severity: 'warning', alternative: 'chmod 755 for directories, chmod 644 for files' },
  { pattern: /chmod\s+-R\s+777/, title: 'Recursive insecure permissions', body: 'Recursively setting 777 on a directory tree. Major security vulnerability.', severity: 'danger', alternative: 'chmod -R 755 for directories' },
  { pattern: />\s*\/dev\/sd[a-z]/, title: 'Writing directly to disk device', body: 'Redirecting output to a raw disk device will destroy data.', severity: 'danger' },
  { pattern: /mkfs/,            title: 'Formatting disk', body: 'mkfs creates a new filesystem, destroying all existing data on the device.', severity: 'danger' },
  { pattern: /dd\s+/,           title: 'Low-level disk operation', body: 'dd copies data at the block level. Wrong of= target can destroy your disk.', severity: 'danger' },
  { pattern: /git\s+push\s+.*--force(?!\-with-lease)/, title: 'Force push', body: 'git push --force overwrites remote history. Other collaborators may lose work.', severity: 'warning', alternative: 'git push --force-with-lease (safer)' },
  { pattern: /git\s+reset\s+--hard/, title: 'Hard reset', body: 'git reset --hard discards all uncommitted changes permanently.', severity: 'warning', alternative: 'git stash to save changes first' },
  { pattern: /git\s+clean\s+-[a-zA-Z]*f/, title: 'Force clean untracked files', body: 'git clean -f permanently deletes untracked files.', severity: 'warning', alternative: 'git clean -n for dry-run first' },
  { pattern: /:\(\)\{\s*:\|:\s*&\s*\};\s*:/, title: 'Fork bomb detected', body: 'This is a fork bomb that will crash your system by spawning infinite processes.', severity: 'danger' },
  { pattern: />\s*\/etc\//, title: 'Overwriting system config', body: 'Redirecting output to /etc/ may overwrite critical system configuration.', severity: 'danger' },
  { pattern: /curl\s+.*\|\s*(sudo\s+)?bash/, title: 'Piping curl to bash', body: 'Executing remote scripts without inspection is a security risk.', severity: 'warning', alternative: 'Download first, inspect, then execute' },
  { pattern: /wget\s+.*\|\s*(sudo\s+)?bash/, title: 'Piping wget to bash', body: 'Executing remote scripts without inspection is a security risk.', severity: 'warning', alternative: 'Download first, inspect, then execute' },
  { pattern: /sudo\s+rm/, title: 'Root-level deletion', body: 'Deleting files as superuser bypasses permission safeguards.', severity: 'warning' },
  { pattern: /iptables\s+-F/, title: 'Flushing firewall rules', body: 'This removes all firewall rules, potentially exposing the system.', severity: 'danger' },
  { pattern: /shutdown|reboot|init\s+[06]/, title: 'System shutdown/reboot', body: 'This will shut down or reboot the system.', severity: 'warning' },
  { pattern: /rsync\s+.*--delete/, title: 'Rsync with delete', body: 'rsync --delete removes files at the destination that are not in the source.', severity: 'warning', alternative: 'rsync --dry-run --delete to preview first' },
  { pattern: /npm\s+publish/, title: 'Publishing to npm', body: 'This will publish the package publicly. Verify version and contents.', severity: 'warning', alternative: 'npm publish --dry-run to preview first' },
  { pattern: /docker\s+(rm|rmi)\s+-f/, title: 'Force removing Docker resources', body: 'Force-removing containers/images cannot be undone.', severity: 'warning' },
  { pattern: /docker\s+system\s+prune/, title: 'Docker system prune', body: 'Removes all unused containers, networks, images, and optionally volumes.', severity: 'warning', alternative: 'docker system prune --dry-run' },
];

const COMMON_ERRORS: Array<{ pattern: RegExp; title: string; body: string; alternative?: string }> = [
  { pattern: /command not found/i, title: 'Command not found', body: 'The command is not installed or not in your PATH. Try installing it with your package manager.', alternative: 'brew install / apt install / npm install -g' },
  { pattern: /permission denied/i, title: 'Permission denied', body: 'You lack permission for this operation. You may need to adjust file permissions or use sudo.', alternative: 'sudo or chmod' },
  { pattern: /EADDRINUSE|address already in use/i, title: 'Port already in use', body: 'Another process is using this port. Find and stop it.', alternative: 'lsof -i :PORT to find the process, then kill PID' },
  { pattern: /ENOENT|no such file or directory/i, title: 'File or directory not found', body: 'The specified path does not exist. Check for typos or verify the working directory.' },
  { pattern: /EACCES/i, title: 'Access denied', body: 'Node.js could not access this resource. Check file ownership and permissions.' },
  { pattern: /ECONNREFUSED/i, title: 'Connection refused', body: 'The target server is not accepting connections. Verify the service is running and the address is correct.' },
  { pattern: /ETIMEDOUT|timeout/i, title: 'Connection timed out', body: 'The connection took too long. Check network connectivity and the target server.' },
  { pattern: /fatal: not a git repository/i, title: 'Not a git repo', body: 'This directory is not a git repository.', alternative: 'git init to initialize, or cd to the correct directory' },
  { pattern: /merge conflict/i, title: 'Merge conflict', body: 'Git found conflicting changes that need manual resolution.', alternative: 'Edit the conflicting files, then git add and git commit' },
  { pattern: /ENOMEM|out of memory|Cannot allocate/i, title: 'Out of memory', body: 'The system ran out of available memory. Close other applications or increase available memory.' },
  { pattern: /disk.*(full|space|quota)/i, title: 'Disk space issue', body: 'Not enough disk space. Free up space or increase storage.', alternative: 'df -h to check, du -sh * to find large directories' },
  { pattern: /npm ERR!.*404/i, title: 'npm package not found', body: 'The requested package does not exist on the registry. Check the package name for typos.' },
  { pattern: /ModuleNotFoundError|Cannot find module/i, title: 'Module not found', body: 'A required module is missing.', alternative: 'npm install / pip install to add the dependency' },
  { pattern: /SyntaxError/i, title: 'Syntax error', body: 'There is a syntax error in the code or command. Check for typos, missing brackets, or quotes.' },
  { pattern: /segmentation fault/i, title: 'Segmentation fault', body: 'The program accessed invalid memory. This is usually a bug in the program itself.' },
];

const POST_SUCCESS_HINTS: Array<{ pattern: RegExp; title: string; body: string }> = [
  { pattern: /^git\s+init/,    title: 'Repository initialized', body: 'Next: create a .gitignore, add files with git add, and make your first commit.' },
  { pattern: /^git\s+clone/,   title: 'Repository cloned', body: 'Next: cd into the directory and explore with ls or check branches with git branch -a.' },
  { pattern: /^npm\s+init/,    title: 'Package initialized', body: 'Next: install dependencies with npm install and start coding.' },
  { pattern: /^ssh-keygen/,    title: 'SSH key generated', body: 'Add the public key to your server or Git provider (e.g., GitHub > Settings > SSH Keys).' },
  { pattern: /^docker\s+build/, title: 'Image built', body: 'Run it with: docker run IMAGE_NAME. Use -p to expose ports, -v for volumes.' },
];

// ═══ §3 STATE MANAGEMENT ═══

export function createHintState(enabled: boolean = true): HintState {
  return { enabled, hints: [], suppressedCommands: [], detailLevel: 'standard' };
}

export function enableHints(state: HintState): HintState {
  return { ...state, enabled: true };
}

export function disableHints(state: HintState): HintState {
  return { ...state, enabled: false };
}

export function setDetailLevel(state: HintState, level: HintState['detailLevel']): HintState {
  return { ...state, detailLevel: level };
}

export function addHint(state: HintState, hint: CommandHint): HintState {
  return { ...state, hints: [...state.hints, hint] };
}

export function dismissHint(state: HintState, index: number): HintState {
  if (index < 0 || index >= state.hints.length) return state;
  const hints = state.hints.map((h, i) => i === index ? { ...h, dismissed: true } : h);
  return { ...state, hints };
}

export function dismissAllHints(state: HintState): HintState {
  return { ...state, hints: state.hints.map(h => ({ ...h, dismissed: true })) };
}

export function suppressCommand(state: HintState, command: string): HintState {
  const base = extractBaseCommand(command);
  if (state.suppressedCommands.includes(base)) return state;
  return { ...state, suppressedCommands: [...state.suppressedCommands, base] };
}

export function unsuppressCommand(state: HintState, command: string): HintState {
  const base = extractBaseCommand(command);
  return { ...state, suppressedCommands: state.suppressedCommands.filter(c => c !== base) };
}

export function isCommandSuppressed(state: HintState, command: string): boolean {
  const base = extractBaseCommand(command);
  return state.suppressedCommands.includes(base);
}

// ═══ §4 HINT GENERATORS ═══

export function generatePreExecutionHint(command: string): CommandHint | null {
  const trimmed = command.trim();
  if (!trimmed) return null;

  for (const dp of DANGEROUS_PATTERNS) {
    if (dp.pattern.test(trimmed)) {
      return {
        type: 'pre-execution',
        severity: dp.severity,
        title: dp.title,
        body: dp.body,
        command: trimmed,
        suggestedAlternative: dp.alternative,
        dismissed: false,
      };
    }
  }

  const base = extractBaseCommand(trimmed);
  const info = COMMANDS[base];
  if (info && info.risk === 'dangerous') {
    return {
      type: 'safety-warning',
      severity: 'warning',
      title: `${base} is a high-risk command`,
      body: `${info.description}. Scope: ${info.scope}. Verify arguments before running.`,
      command: trimmed,
      dismissed: false,
    };
  }

  if (/sudo\s+/.test(trimmed)) {
    return {
      type: 'pre-execution',
      severity: 'warning',
      title: 'Running as superuser',
      body: 'This command will execute with elevated privileges. Verify it is correct.',
      command: trimmed,
      dismissed: false,
    };
  }

  return null;
}

export function generateTypingHint(partialCommand: string): CommandHint | null {
  const trimmed = partialCommand.trim();
  if (!trimmed || trimmed.length < 2) return null;

  const base = extractBaseCommand(trimmed);
  const info = COMMANDS[base];
  if (!info || !info.typingHint) return null;

  return {
    type: 'typing',
    severity: 'info',
    title: `${base} — ${info.description}`,
    body: info.typingHint,
    command: base,
    relatedDocs: `man ${base}`,
    dismissed: false,
  };
}

export function generatePostErrorHint(command: string, errorOutput: string): CommandHint | null {
  const trimmed = command.trim();
  if (!errorOutput) return null;

  for (const err of COMMON_ERRORS) {
    if (err.pattern.test(errorOutput)) {
      return {
        type: 'post-error',
        severity: 'suggestion',
        title: err.title,
        body: err.body,
        command: trimmed,
        suggestedAlternative: err.alternative,
        dismissed: false,
      };
    }
  }

  return null;
}

export function generatePostSuccessHint(command: string, _output: string): CommandHint | null {
  const trimmed = command.trim();

  for (const s of POST_SUCCESS_HINTS) {
    if (s.pattern.test(trimmed)) {
      return {
        type: 'post-success',
        severity: 'info',
        title: s.title,
        body: s.body,
        command: trimmed,
        dismissed: false,
      };
    }
  }

  return null;
}

// ═══ §5 COMMAND EXPLANATION ═══

export function explainCommand(command: string): CommandExplanation {
  const trimmed = command.trim();
  const parts = splitCommandParts(trimmed);
  const base = parts.base;
  const info = COMMANDS[base];

  const flags: FlagInfo[] = [];
  const args: Array<{ name: string; description: string }> = [];

  for (const token of parts.tokens) {
    if (token.startsWith('-')) {
      const knownFlag = info?.commonFlags[token];
      if (knownFlag) {
        flags.push(knownFlag);
      } else {
        flags.push({ flag: token, description: `Flag: ${token}`, isDestructive: false, isSudo: false });
      }
    } else if (token !== base) {
      args.push({ name: token, description: guessArgumentDescription(base, token) });
    }
  }

  const hasSudo = trimmed.startsWith('sudo ');
  const risk = info?.risk ?? (hasSudo ? 'moderate' : 'safe');
  const hasDestructiveFlag = flags.some(f => f.isDestructive);
  const effectiveRisk: 'safe' | 'moderate' | 'dangerous' =
    hasDestructiveFlag && risk === 'safe' ? 'moderate' :
    hasDestructiveFlag && risk === 'moderate' ? 'dangerous' : risk;

  return {
    command: base,
    description: info?.description ?? `Execute ${base}`,
    flags,
    arguments: args,
    riskLevel: effectiveRisk,
    affectedScope: info?.scope ?? (hasSudo ? 'system-wide' : 'current context'),
  };
}

export function explainFlag(command: string, flag: string): FlagInfo {
  const base = extractBaseCommand(command);
  const info = COMMANDS[base];
  const normalized = flag.startsWith('-') ? flag : `-${flag}`;

  if (info?.commonFlags[normalized]) {
    return info.commonFlags[normalized];
  }

  return {
    flag: normalized,
    description: `Flag ${normalized} for ${base}`,
    isDestructive: false,
    isSudo: false,
  };
}

// ═══ §6 QUERIES ═══

export function getActiveHints(state: HintState): CommandHint[] {
  return state.hints.filter(h => !h.dismissed);
}

export function getHintsByType(state: HintState, type: HintType): CommandHint[] {
  return state.hints.filter(h => h.type === type);
}

export function getHintSummary(state: HintState): HintSummary {
  const byType: Record<HintType, number> = {
    'pre-execution': 0, 'typing': 0, 'post-error': 0, 'post-success': 0, 'flag-info': 0, 'safety-warning': 0,
  };
  const bySeverity: Record<HintSeverity, number> = { info: 0, suggestion: 0, warning: 0, danger: 0 };

  for (const h of state.hints) {
    byType[h.type]++;
    bySeverity[h.severity]++;
  }

  return { total: state.hints.length, byType, bySeverity };
}

export function shouldShowHint(state: HintState, command: string): boolean {
  if (!state.enabled) return false;
  return !isCommandSuppressed(state, command);
}

// ═══ §7 HELPERS ═══

function extractBaseCommand(command: string): string {
  const trimmed = command.trim();
  const withoutSudo = trimmed.startsWith('sudo ') ? trimmed.slice(5).trim() : trimmed;
  const firstSpace = withoutSudo.indexOf(' ');
  return firstSpace === -1 ? withoutSudo : withoutSudo.slice(0, firstSpace);
}

function splitCommandParts(command: string): { base: string; tokens: string[] } {
  const withoutSudo = command.trim().startsWith('sudo ') ? command.trim().slice(5).trim() : command.trim();
  const tokens: string[] = [];
  let current = '';
  let inQuote: string | null = null;

  for (let i = 0; i < withoutSudo.length; i++) {
    const ch = withoutSudo[i];
    if (inQuote) {
      if (ch === inQuote) { inQuote = null; } else { current += ch; }
    } else if (ch === '"' || ch === "'") {
      inQuote = ch;
    } else if (ch === ' ' || ch === '\t') {
      if (current) { tokens.push(current); current = ''; }
    } else {
      current += ch;
    }
  }
  if (current) tokens.push(current);

  const base = tokens[0] ?? '';

  // Merge combined short flags like -rf into a single token for lookup
  return { base, tokens };
}

function guessArgumentDescription(base: string, arg: string): string {
  if (arg.startsWith('/') || arg.startsWith('./') || arg.startsWith('~')) return 'File/directory path';
  if (arg.includes('.') && !arg.startsWith('-')) return 'File or host';
  if (/^\d+$/.test(arg)) return base === 'kill' ? 'Process ID' : 'Numeric argument';
  if (arg.includes('@')) return 'User/host reference';
  if (arg.includes(':')) return 'Remote path or port mapping';
  return 'Argument';
}
